/**
 * SeriesBreadcrumb — Phase 119.E series-builder.
 *
 * Surfaces the chain of lessons sharing this lesson's series_root_id
 * as a horizontal row of clickable pills:
 *
 *     Lesson 1  →  [Lesson 2]  →  Lesson 3
 *
 * The current lesson is highlighted (filled mint pill). Each other
 * pill links to that lesson's workspace. Hidden when the activity
 * isn't part of any series (root has no descendants).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Sparkles } from "lucide-react";
import { getActivitySeries } from "../lib/api";

export const SeriesBreadcrumb = ({ activityId }) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!activityId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const out = await getActivitySeries(activityId);
        if (cancelled) return;
        setItems(out?.items || []);
      } catch (_e) {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activityId]);

  if (!items || items.length < 2) return null;  // Need ≥2 to be a series

  return (
    <div
      className="flex items-center flex-wrap gap-1.5 mb-3"
      data-testid="lesson-workspace-series-breadcrumb"
    >
      <span className="inline-flex items-center gap-1 rounded-full bg-[#96ded1]/25 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.15em] text-[#1c2d3d]">
        <Sparkles className="h-2.5 w-2.5" />
        Series · {items.length} lessons
      </span>
      {items.map((it, idx) => (
        <span key={it.id} className="inline-flex items-center gap-1.5">
          {it.is_current ? (
            <span
              data-testid={`series-pill-current-${it.id}`}
              className="inline-flex items-center max-w-[200px] truncate rounded-md border border-[#1c2d3d] bg-[#1c2d3d] px-2 py-1 text-[11px] font-extrabold text-white"
              title={it.activity_name}
            >
              {idx + 1}. {it.activity_name}
            </span>
          ) : (
            <Link
              to={`/lesson/${it.id}`}
              data-testid={`series-pill-${it.id}`}
              className="inline-flex items-center max-w-[200px] truncate rounded-md border border-[#4a6984]/30 bg-white px-2 py-1 text-[11px] font-bold text-[#4a6984] hover:border-[#1c2d3d] hover:text-[#1c2d3d]"
              title={it.activity_name}
            >
              {idx + 1}. {it.activity_name}
            </Link>
          )}
          {idx < items.length - 1 ? <ChevronRight className="h-3 w-3 text-[#4a6984]/50" /> : null}
        </span>
      ))}
    </div>
  );
};

export default SeriesBreadcrumb;
